import { useState } from "react";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage, formatError } from "@/lib/api";

export default function ImageUploader({ value, onChange, testid = "image-uploader", aspect = "aspect-video" }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className={`${aspect} relative rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 overflow-hidden group`}>
        {value ? (
          <img src={resolveImage(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-slate-400 text-xs">No image</div>
        )}

        {/* Upload overlay */}
        <label className="absolute inset-0 grid place-items-center bg-slate-900/50 opacity-0 group-hover:opacity-100 transition cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testid}-input`}
          />
          <div className="px-4 py-2 rounded-full bg-white text-[#1E3A8A] text-xs font-semibold inline-flex items-center gap-2 shadow">
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...</> : <><Upload className="w-3.5 h-3.5" /> {value ? "Replace Image" : "Upload Image"}</>}
          </div>
        </label>

        {/* Remove button — visible only when there's an image */}
        {value && !uploading && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 hover:bg-red-600 hover:text-white text-red-600 grid place-items-center shadow-md transition z-10"
            aria-label="Remove image"
            data-testid={`${testid}-remove`}
            title="Remove image"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL"
          data-testid={`${testid}-url`}
          className="flex-1 text-xs rounded-lg border border-slate-200 px-3 py-2 focus:border-[#1E3A8A] focus:outline-none"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs font-semibold text-red-600 hover:bg-red-50 px-2.5 py-2 rounded-lg transition inline-flex items-center gap-1"
            data-testid={`${testid}-clear`}
            title="Remove image"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
